/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, memo, useContext } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx, type SerializedStyles } from '@emotion/react';

import { Stack, Text } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { getTokenListColumns } from '../get-token-list-columns';
import type { TransformedTokenMerged } from '../types';
import NoResults from './no-results';
import { TableColumnsContext } from './table-columns-context';
import { tableTokenColumnWidthStyles } from './table-token-column-width-styles';
import TokenRow from './token-row';

const tableHeaderStyles = css({
	position: 'sticky',
	zIndex: 1,
	backgroundColor: token('elevation.surface'),
});

const tableStyles = css({
	display: 'flex',
	boxSizing: 'border-box',
	justifyContent: 'stretch',
	gap: token('space.200'),
	flexDirection: 'column',
	border: 'none',
});

const tableHeaderRowStyles = css({
	display: 'flex',
	padding: `${token('space.050')} 0`,
	gap: token('space.200'),
	borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
});

const resetDefaultStyles = css({
	border: 'none',
});

const tableHeaderValueColumnStyles = css({
	padding: 0,
	[media.below.sm]: {
		display: 'none',
	},
});

const tableHeaderFirstColumn = css({
	width: '80%',
	padding: 0,
});

interface TokenListProps {
	/**
	 * Offset the sticky table header to avoid overlapping existing fixed elements.
	 */
	scrollOffset?: number;
	list?: TransformedTokenMerged[];
	isLoading?: boolean;
	isInSearchResult?: boolean;
	testId?: string;
	label?: string;
}

const TokenTableComponent = ({
	list,
	scrollOffset = 0,
	isLoading,
	isInSearchResult = false,
	testId,
	label,
}: TokenListProps): React.JSX.Element => {
	if (list !== undefined && list.length === 0) {
		return <NoResults />;
	}

	const group = list && list[0].attributes.group;
	const columns = getTokenListColumns(group);
	return (
		<TableColumnsContext.Provider value={columns}>
			<Stack space="space.200" testId={testId && `${testId}-token-list`}>
				<table css={tableStyles} aria-labelledby={label}>
					{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
					<thead css={[resetDefaultStyles, tableHeaderStyles, { top: scrollOffset }]}>
						<ListHeader />
					</thead>
					<tbody css={resetDefaultStyles}>
						{isLoading ? (
							<Fragment>
								<TokenRow key={1} isLoading />
								<TokenRow key={2} isLoading />
								<TokenRow key={3} isLoading />
								<TokenRow key={4} isLoading />
								<TokenRow key={5} isLoading />
							</Fragment>
						) : (
							list?.map((tokenItem) => (
								<TokenRow
									key={tokenItem.name}
									token={tokenItem}
									testId={testId}
									isInSearchResult={isInSearchResult}
								/>
							))
						)}
					</tbody>
				</table>
			</Stack>
		</TableColumnsContext.Provider>
	);
};

const ListHeader = () => {
	const columns = useContext(TableColumnsContext);
	return (
		<tr css={tableHeaderRowStyles}>
			<ColumnHeader columnName={columns[0].label} styles={tableHeaderFirstColumn} />
			{columns.slice(1).map(({ label, width = 'medium' }) => (
				<ColumnHeader
					key={label}
					columnName={label}
					styles={[tableHeaderValueColumnStyles, tableTokenColumnWidthStyles[width]]}
				/>
			))}
		</tr>
	);
};

interface ColumnHeaderProps {
	columnName: string;
	styles: SerializedStyles | SerializedStyles[];
}

const ColumnHeader = ({ columnName, styles }: ColumnHeaderProps) => (
	// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
	<th scope="col" css={styles}>
		<Text as="strong">{columnName}</Text>
	</th>
);

const TokenTable: React.MemoExoticComponent<
	({
		list,
		scrollOffset,
		isLoading,
		isInSearchResult,
		testId,
		label,
	}: TokenListProps) => React.JSX.Element
> = memo(TokenTableComponent);

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default TokenTable;
